import { z } from 'zod';

const k6Load = {
  vus: z.number().int().min(1).max(200),
  durationSeconds: z.number().int().min(10).max(300),
  as: z.string().min(1).optional(),
};

type Estimable = {
  rounds: number;
  lighthouse?: { pages: readonly unknown[] };
  k6?: { durationSeconds: number };
};

/** Estimated time for both arms, including one warm-up, setup, and collection. */
export const estimatePerfSeconds = (config: Estimable): number =>
  (config.rounds + 1) *
    ((config.lighthouse?.pages.length ?? 0) * 45 + (config.k6?.durationSeconds ?? 0) + 30) *
    2 +
  600;

const perfBase = {
  rounds: z.number().int().min(5).max(15).default(7),
  lighthouse: z
    .strictObject({
      formFactor: z.enum(['desktop', 'mobile']),
      pages: z
        .array(z.strictObject({
          name: z.string().min(1).max(40).regex(/^[a-z0-9-]+$/),
          path: z
            .string()
            .startsWith('/')
            .refine((path) => !path.startsWith('//'), 'path must be relative, not protocol-relative')
            .refine((path) => !path.includes('\\'), 'path must not contain backslashes'),
          as: z.string().min(1).optional(),
        }))
        .min(1)
        .max(10),
    })
    .optional(),
};

const withPerfRules = <S extends z.ZodType<Estimable>>(schema: S) =>
  schema
    .refine((config) => config.lighthouse !== undefined || config.k6 !== undefined, {
      message: 'configure at least one of lighthouse or k6',
    })
    .refine((config) => estimatePerfSeconds(config) <= 5400, {
      message: 'estimated run time exceeds 90 minutes; reduce rounds, pages or duration',
    });

/** `perf.yaml` as written; the CLI resolves `k6.script` relative to this file. */
export const PerfConfigSchema = withPerfRules(
  z.strictObject({
    ...perfBase,
    k6: z.strictObject({ script: z.string().min(1), ...k6Load }).optional(),
  })
);

/** Config received and stored by the API after the CLI inlines the k6 script. */
export const StoredPerfConfigSchema = withPerfRules(
  z.strictObject({
    ...perfBase,
    k6: z.strictObject({ scriptSource: z.string().min(1).max(200_000), ...k6Load }).optional(),
  })
);

export type PerfConfig = z.infer<typeof PerfConfigSchema>;
export type StoredPerfConfig = z.infer<typeof StoredPerfConfigSchema>;
